/* eslint-disable no-magic-numbers -- for now*/

/* eslint-disable promise/avoid-new -- for now */
// src/services/abstracts/fileencryption.service.interface.ts (Cleaned)

import type { RecipientEncryptionResult, RecipientKey } from "@/types/poc/auth";

export interface EncryptionResult {
  encryptedFile: File;
  encryptedFileKey: File;
  recipientResults: RecipientEncryptionResult[];
  originalFileName: string;
  originalFileSize: number;
  originalFileType: string;
}

export interface DecryptionResult {
  decryptedFile: File;
  originalFileName: string;
  originalFileSize: number;
  originalFileType: string;
}

export interface ExtractedComponents {
  data: Uint8Array;
  iv: Uint8Array;
  authTag: Uint8Array;
}

/**
 * File encryption service focused on high-level file operations
 * Delegates crypto primitives to CryptoService, focuses on file workflows
 */
export abstract class FileEncryptionService {

  // =================== ABSTRACT METHODS (Core File Workflows) ===================

  /**
   * Encrypt file with AES-GCM-256 and prepare for multiple recipients
   * @param file The file to encrypt
   * @param ukek User Key Encrypted Key for owner access
   * @param recipients Array of recipients with their public keys
   * @returns Complete encryption result with encrypted file, key, and recipient data
   */
  abstract encryptFile(
    file: File,
    ukek: Uint8Array,
    recipients: Array<{ id: string, publicKey: Uint8Array }>
  ): Promise<EncryptionResult>;

  /**
   * Decrypt file with both encrypted file and encrypted key file
   */
  abstract decryptFileWithEncryptedKey(
    encryptedFile: File,
    encryptedFileKey: File,
    ukek: Uint8Array
  ): Promise<DecryptionResult>;

  /**
   * Encrypt file key for specified recipients using ML-KEM
   */
  abstract encryptFileKeyForRecipients(
    fileKey: Uint8Array,
    recipients?: RecipientKey[]
  ): Promise<RecipientEncryptionResult[]>;

  /**
   * Decrypt file key for a specific recipient
   */
  abstract decryptFileKeyForRecipient(
    recipientResult: RecipientEncryptionResult,
    recipientPrivateKey: Uint8Array
  ): Promise<Uint8Array>;

  /**
   * Decrypt a file key from a facilitator's encryption data and re-encrypt it
   * for a new recipient. Used for file key facilitation during account recovery.
   * @param facilitatorEncryption The facilitator's encryption data (cipherText + encryptedFileKey)
   * @param facilitatorPrivateKey The facilitator's ML-KEM private key
   * @param recipientPublicKey The new recipient's ML-KEM public key
   * @returns Encryption data for the new recipient (same blob shape, no recipientId)
   */
  abstract reEncryptFileKeyForRecipient(
    facilitatorEncryption: RecipientEncryptionResult,
    facilitatorPrivateKey: Uint8Array,
    recipientPublicKey: Uint8Array
  ): Promise<{ cipherText: Uint8Array; encryptedFileKey: { encryptedData: Uint8Array; iv: Uint8Array; authTag: Uint8Array } }>;

  /**
   * Decrypt file as a recipient using ML-KEM private key
   */
  abstract decryptFileAsRecipient(
    encryptedFile: File,
    recipientResult: RecipientEncryptionResult,
    recipientPrivateKey: Uint8Array
  ): Promise<DecryptionResult>;

  // =================== FILE-SPECIFIC OPERATIONS ===================

  /**
   * Read file as Uint8Array
   */
  static async readFileAsUint8Array(file: File): Promise<Uint8Array> {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(new Uint8Array(reader.result));
        } else {
          reject(new Error('Failed to read file as ArrayBuffer'));
        }
      };

    reader.onerror = () => { reject(new Error(reader.error?.message ?? 'FileReader error')); };
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Convert Blob to Uint8Array
   */
  static async blobToUint8Array(blob: Blob): Promise<Uint8Array> {
    const arrayBuffer = await blob.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  }

  /**
   * Combine encrypted components into a file-friendly format
   * Format: [IV_LENGTH(2)] [IV] [TAG_LENGTH(2)] [TAG] [ENCRYPTED_DATA]
   */
  static combineEncryptedComponents(
    encryptedData: Uint8Array,
    iv: Uint8Array,
    authTag: Uint8Array
  ): Blob {
    // Calculate total size: 2 bytes for IV length + IV + 2 bytes for tag length + tag + data
    const totalSize = 2 + iv.length + 2 + authTag.length + encryptedData.length;

    const buffer = new ArrayBuffer(totalSize);
    const view = new Uint8Array(buffer);

    let offset = 0;

    // Write IV length (big-endian uint16)
    view[offset++] = (iv.length >> 8) & 0xFF;
    view[offset++] = iv.length & 0xFF;

    // Write IV
    view.set(iv, offset);
    offset += iv.length;

    // Write auth tag length (big-endian uint16)
    view[offset++] = (authTag.length >> 8) & 0xFF;
    view[offset++] = authTag.length & 0xFF;

    // Write auth tag
    view.set(authTag, offset);
    offset += authTag.length;

    // Write encrypted data
    view.set(encryptedData, offset);

    return new Blob([buffer]);
  }

  /**
   * Extract encrypted data components from combined format
   */
  static extractEncryptedComponents(combined: Uint8Array): ExtractedComponents {
    // Extract IV length (first 2 bytes, big-endian)
    const ivLength = (combined[0] << 8) | combined[1];
    const iv = combined.slice(2, 2 + ivLength);

    // Extract auth tag length
    const authTagLengthOffset = 2 + ivLength;
    const authTagLength = (combined[authTagLengthOffset] << 8) |
                         combined[authTagLengthOffset + 1];

    // Extract auth tag
    const authTagOffset = authTagLengthOffset + 2;
    const authTag = combined.slice(authTagOffset, authTagOffset + authTagLength);

    // Extract encrypted data
    const dataOffset = authTagOffset + authTagLength;
    const data = combined.slice(dataOffset);

    return { data, iv, authTag };
  }
}
